import { html, FIRST_LOAD_DELAY } from '../utils/environment'
import { module } from 'modujs'

const ONE_SECOND_IN_MILLISECONDS = 1000

const MIN_DELAY = 0
const MAX_DELAY = (15 * ONE_SECOND_IN_MILLISECONDS)

let SHOWN_POPUPS = 0

/**
 * Popup/Popover
 *
 * Ideally, the module should be assigned a unique identifier,
 * either via the `id` attribute or with an explicit module ID:
 *
 * 1. `<div class="c-popup" data-module-popup></div>`
 * 2. `<div class="c-popup" data-module-popup="foobar" data-popup-delay="15"></div>`
 * 3. `<div class="c-popup" data-module-popup id="bazqux" data-popup-date="19701231"></div>`
 *
 * Local storage keys:
 *
 * 1. `popup`
 * 2. `popup.foobar`
 * 3. `popup.bazqux.19701231`
 */
export default class extends module {
    /** @type {?number} The delay in milliseconds. */
    #delay = null

    /** @type {?number} The {@see window.setTimeout()} identifier. */
    #delayID = null

    /** @type {?string} The popup name. */
    #dismissID = null

    /** @type {?string} The authored module ID. */
    #moduleID = null

    /** @type {int} The number of time the popup was dismissed by the user */
    #dismissCount = 0

    /** @type {int} The number of time the popup has to be dismissed before never appearing again. */
    #dismissLimit = 1

    /**
     * @return number
     */
    get delay() {
        if (this.#delay == null) {
            const delay = Number.parseInt(this.getData('delay'))
            if (Number.isNaN(delay)) {
                this.#delay = 0
            } else {
                this.#delay = Math.min(MAX_DELAY, Math.max(MIN_DELAY, (delay * ONE_SECOND_IN_MILLISECONDS)))
            }
        }

        return this.#delay
    }

    /**
     * @return string
     */
    get dismissID() {
        if (this.#dismissID == null) {
            const segments = [ 'popup-dismisses' ]

            if (this.#moduleID) {
                segments.push(this.#moduleID)
            }

            const date = this.getData('date')
            if (date) {
                segments.push(date)
            }

            this.#dismissID = segments.join('.')
        }

        return this.#dismissID
    }

    constructor(options) {
        super(options)

        this.#moduleID = (this.el.getAttribute(`data-module-${options.dataName}`) || this.el.id || null)

        this.dismiss = this.dismiss.bind(this)

        // Set dismiss limit
        const dismissLimit = parseInt(this.getData('dismiss-limit'))
        if (dismissLimit) {
            this.#dismissLimit = dismissLimit
        }

        // Set number of times the popup was dismissed by the user.
        const dismissCount = parseInt(window.localStorage.getItem(this.dismissID))
        if (dismissCount > 0) {
            this.#dismissCount = dismissCount
        }
    }

    init() {
        this.maybeShow()

        this.$('dismiss').forEach((element) => element.addEventListener('click', this.dismiss))
    }

    dismiss() {
        this.hide()
        window.localStorage.setItem(this.dismissID, this.#dismissCount + 1)
    }

    hide() {
        this.clearDelay()

        SHOWN_POPUPS--

        if (SHOWN_POPUPS === 0) {
            html.classList.remove('has-popup')
        }

        this.el.classList.remove('-open')
    }

    show() {
        this.clearDelay()

        if (SHOWN_POPUPS === 0) {
            html.classList.add('has-popup')
        }

        SHOWN_POPUPS++

        this.el.classList.add('-open')
    }

    delayShow() {
        if (this.delay) {
            let delay = this.delay
            if (window.firstHit) {
                delay += FIRST_LOAD_DELAY
            }

            this.#delayID = window.setTimeout(() => this.show(), delay)
        } else {
            this.show()
        }
    }

    maybeShow() {
        if (this.#moduleID && !html.dataset.showPopup?.split(' ').includes(this.#moduleID)) {
            return
        }

        if (this.#dismissCount >= this.#dismissLimit) {
            return
        }

        this.delayShow()
    }

    clearDelay() {
        if (this.#delayID) {
            window.clearTimeout(this.#delayID)
            this.#delayID = null
        }
    }

    destroy() {
        this.hide()

        this.$('dismiss').forEach((element) => element.removeEventListener('click', this.dismiss))
    }
}
